import { createFileRoute } from '@tanstack/react-router';

import { isLoginMode, LoginPage, type LoginMode } from '../auth/login-page';
import { useAppNavigate } from '../lib/session/navigate';
import { DEFAULT_NEXT_PATH, safeNextPath } from '../lib/session/next-path';

/**
 * `/login?mode=code|password|link&next=/account`
 *
 * The route owns the URL and the navigation; `LoginPage` owns everything else, so it
 * renders in a test without a router (the same split as `BookPage`, M5.3). `next` is
 * validated here, at the edge, so nothing downstream has to wonder whether it is safe.
 */

interface LoginSearch {
  mode?: LoginMode;
  next?: string;
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    ...(isLoginMode(search['mode']) ? { mode: search['mode'] } : {}),
    ...(typeof search['next'] === 'string'
      ? { next: safeNextPath(search['next'], DEFAULT_NEXT_PATH) }
      : {}),
  }),
  component: LoginRoute,
});

function LoginRoute() {
  const { mode, next } = Route.useSearch();
  const navigate = useAppNavigate();

  return (
    <LoginPage
      {...(mode === undefined ? {} : { initialMode: mode })}
      next={next ?? DEFAULT_NEXT_PATH}
      onSignedIn={() => {
        navigate(next ?? DEFAULT_NEXT_PATH, { replace: true });
      }}
      onNavigate={navigate}
    />
  );
}
